import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../../../components/axiosInstance";
import { API_URLS } from "../../../../apiUrl";
import { Pagination } from "@mui/material";
import {
  generateTamraliptaReceipt,
  numToWords,
  type TamraliptaReceiptData,
} from "../../../../components/generateTamraliptaReceipt ";



const useAnimatedCount = (target: number, duration = 1200) => {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (target === 0) { setCount(0); return; }
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  return count;
};

const billMonthToLabel = (billMonth: string): string => {
  const date = new Date(billMonth);
  const mn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${mn[date.getMonth()]}-${String(date.getFullYear()).slice(2)}`;
};

const getTodayISO = () => {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

interface BillEntry {
  id: number; water_connection_id: number; bill_month: string; bill_amount: number;
  due_date: string; is_paid: boolean; paid_date: string | null;
  consumer_transaction_id: string | null; created_at: string; updated_at: string;
  bill_number: string; collector_id: number | null;
}
interface BillMapping {
  label: string; billMonth: string; amount: number; billId: number; billNumber: string; selected: boolean;
}
interface Consumer {
  connectionNo: string; holdingNo: string; category: string; ferule: string;
  arrear: number; name?: string; is_opening_due_cleared?: boolean; bills: Record<string, BillEntry>; phone_number: any;
  advance_amount?: number;
}
interface MonthlyDataItem {
  mapping_id: number; water_connection_bill_id: number; monthly_charge_amount?: number;
  arrear_amount?: number; is_arrear_bill?: boolean; bill_month: string;
}
interface MappedListEntry {
  connection_number: string; category_of_consumer: string; collector_id: number; rate: string;
  arrear_due_till_march: string; total_monthly_charge_amount: string; receipt_number: string;
  total_month_number: string; total_amount_at_this_stage: string; available_amount_at_this_stage: string;
  ferule_size: string; water_connection_id: number; monthly_data: MonthlyDataItem[];
  created_at: string; consumer_name: string; holding_number?: string; ward_number?: string; ward_no?: any;
  phone_number?: any;
  receipt_date?: any;
  total_advance_amount?: string | number;
  advance_amount?: number | null;
}
interface CollectorDetails {
  street_id: number; total_deposit?: number; available_balance?: number | string;
  role_name: string; last_3_months_mapped_amount: number; daily_mapped_amount: number;
  total_in_current_fy: number; total_mapped_consumer_today: number; [key: string]: unknown;
}
interface ConsumerListItem {
  connection_number: string;
  consumer_name: string;
  holding_number?: string;
  category?: string;
  consumer_category_name?: string;
  ward_name?: string;
  ward_number?: string;
  street_name?: string;
  street_id?: number;
  street_no?: string;
  phone_number?: any;
}

const AnimatedCards = ({ data }: { data: CollectorDetails | null }) => {
  const animDaily     = useAnimatedCount(Number(data?.daily_mapped_amount ?? 0));
  const animMonthly   = useAnimatedCount(Number(data?.last_3_months_mapped_amount ?? 0));
  const animFy        = useAnimatedCount(Number(data?.total_in_current_fy ?? 0));
  const animConsumers = useAnimatedCount(Number(data?.total_mapped_consumer_today ?? 0));
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div className="bg-white shadow-md rounded-lg p-5 md:col-span-2">
        <div className="grid grid-cols-3 text-center">
          <div><p className="text-gray-500 text-sm font-semibold border-b pb-1">Last Day</p><p className="text-2xl font-bold text-blue-600 mt-2">₹ {animDaily.toLocaleString()}</p></div>
          <div className="border-x"><p className="text-gray-500 text-sm font-semibold border-b pb-1">Last 3 Month</p><p className="text-2xl font-bold text-purple-600 mt-2">₹ {animMonthly.toLocaleString()}</p></div>
          <div><p className="text-gray-500 text-sm font-semibold border-b pb-1">FY</p><p className="text-2xl font-bold text-green-600 mt-2">₹ {animFy.toLocaleString()}</p></div>
        </div>
      </div>
      <div className="bg-white shadow-md rounded-lg p-5">
        <h4 className="text-gray-600">Consumers Mapped Today</h4>
        <p className="text-3xl font-bold text-purple-600 mt-2">{animConsumers}</p>
      </div>
    </div>
  );
};

const CounterCollector = () => {
  const isSubmittingRef = React.useRef(false);

  const [nameQuery, setNameQuery]                     = useState("");
  const [consumerList, setConsumerList]               = useState<ConsumerListItem[]>([]);
  const [consumerListLoading, setConsumerListLoading] = useState(false);
  const [nameListPage, setNameListPage]               = useState(1);
  const [totalConsumers, setTotalConsumers]           = useState(0);
  const [showNameList, setShowNameList]               = useState(false);
  const nameListPerPage = 5;
  const [totalNamePages, setTotalNamePages]           = useState(0);

  const [consumer, setConsumer]                       = useState<Consumer | null>(null);
  const [billMappings, setBillMappings]               = useState<BillMapping[]>([]);
  const [showConsumerSection, setShowConsumerSection] = useState(false);
  const [consumerNotFound, setConsumerNotFound]       = useState(false);
  const [includeArrear, setIncludeArrear]             = useState(false);
  const [arrearReceipt, setArrearReceipt]             = useState("");
  const [arrearInput, setArrearInput]                 = useState<string>("");
  const [sharedReceipt, setSharedReceipt]             = useState("");
  const [receiptDate, setReceiptDate]                 = useState(getTodayISO());
  const [mappedList, setMappedList]                   = useState<MappedListEntry[]>([]);
  const [selectedMapped, setSelectedMapped]           = useState<MappedListEntry | null>(null);
  const [isSubmitting, setIsSubmitting]               = useState(false);
  const [totalMadePayment, setTotalMadePayment]       = useState<string>("");
  const [isAdjustFromAdvance, setIsAdjustFromAdvance] = useState(false);
  const [adjustFromAdvanceAmount, setAdjustFromAdvanceAmount] = useState<string>("");

  const [cashDetails, setCashDetails]                 = useState<CollectorDetails | null>(null);
  const [mappedPage, setMappedPage]                   = useState(1);
  const [mappedTotalPages, setMappedTotalPages]       = useState(1);
  const mappedRowsPerPage = 50;

  const collectorId = localStorage.getItem("municipalityId") || 0;
  const Street_id   = Number(localStorage.getItem("street_id")) || 0;

  const arrearAmount    = consumer?.is_opening_due_cleared ? 0 : (consumer?.arrear ?? 0);
  const hasArrear       = arrearAmount > 0;
  const selectedBills   = billMappings.filter(m => m.selected);
  const arrearFullyPaid = hasArrear && includeArrear && Number(arrearInput) === arrearAmount;
  const monthTabsEnabled= !hasArrear || arrearFullyPaid;
  const totalEntered    = (hasArrear && includeArrear ? (Number(arrearInput)||0) : 0) + selectedBills.reduce((a,m)=>a+m.amount,0);
  const anythingSelected= (hasArrear && includeArrear) || selectedBills.length > 0;
  const totalPaidNum = Number(totalMadePayment) || 0;
  const adjAmountNum     = isAdjustFromAdvance ? Number(adjustFromAdvanceAmount) || 0 : 0;
  const totalPaymentApplied = totalPaidNum + adjAmountNum;
  const hasRemainingDues = (selectedBills.length < billMappings.length) || (hasArrear && (!includeArrear || Number(arrearInput) < arrearAmount));
  const isAdvanceAllowed = !hasRemainingDues;
  const canSave = totalEntered > 0 && anythingSelected && arrearReceipt.trim() !== "" && (!hasArrear || !includeArrear || Number(arrearInput) > 0) && (isAdvanceAllowed ? totalPaymentApplied >= totalEntered : totalPaymentApplied === totalEntered);

  useEffect(() => {
    const cashRequired = totalEntered - (isAdjustFromAdvance ? (Number(adjustFromAdvanceAmount) || 0) : 0);
    setTotalMadePayment(String(Math.max(0, cashRequired)));
  }, [totalEntered, isAdjustFromAdvance, adjustFromAdvanceAmount]);

  const debounceRef          = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipSearchRef        = useRef(false);
  const consumerSelectedRef  = useRef(false);
  const originalNameQueryRef = useRef("");

  const fetchConsumerList = useCallback(async (query: string, pg: number) => {
    if (!query.trim()) { setConsumerList([]); setTotalConsumers(0); setShowNameList(false); return; }
    setConsumerListLoading(true);
    try {
      const res = await axiosInstance.post(API_URLS.consumerDetail, {
        search: query,
        page: pg,
        limit: nameListPerPage,
        street_id: Street_id,
      });
      const data = res.data?.data;
      setConsumerList(Array.isArray(data?.rows) ? data.rows : []);
      setTotalConsumers(data?.pagination?.totalValues || 0);
      setTotalNamePages(data?.pagination?.lastPage || 0);
      setShowNameList(true);
    } catch {
      setConsumerList([]); setTotalConsumers(0);
    } finally {
      setConsumerListLoading(false);
    }
  }, [Street_id]);

  useEffect(() => {
    if (skipSearchRef.current) { skipSearchRef.current = false; return; }
    if (consumerSelectedRef.current) return;
    if (!nameQuery.trim()) { setShowNameList(false); setConsumerList([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setNameListPage(1);
      fetchConsumerList(nameQuery, 1);
      setShowNameList(true);
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [nameQuery]);

  useEffect(() => {
    if (!showNameList || !nameQuery.trim()) return;
    fetchConsumerList(nameQuery, nameListPage);
  }, [nameListPage]);

  const fetchCollectorDetails = useCallback(async () => {
    try {
      const res  = await axiosInstance.post(API_URLS.collectordetails, { collector_id: collectorId });
      const data = res.data?.data;
      if (data?.role_name === "collector" && data?.street_id) localStorage.setItem("street_id", String(data.street_id));
      setCashDetails(data || null);
    } catch {}
  }, [collectorId]);

  const fetchMappedList = useCallback(async (pg = 1) => {
    try {
      const res = await axiosInstance.post(API_URLS.collectorMappedList, {
        collector_id: collectorId,
        page: pg,
        limit: mappedRowsPerPage,
      });
      const raw        = res.data?.data;
      const rows       = Array.isArray(raw) ? raw : Array.isArray(raw?.rows) ? raw.rows : Array.isArray(raw?.data) ? raw.data : [];
      const pagination = raw?.pagination;
      setMappedList(rows);
      setMappedTotalPages(pagination?.lastPage || 1);
    } catch { setMappedList([]); }
  }, [collectorId]);

  useEffect(() => {
    if (!collectorId) return;
    fetchCollectorDetails();
    fetchMappedList(1);
  }, [collectorId, fetchCollectorDetails, fetchMappedList]);

  useEffect(() => {
    if (!collectorId) return;
    fetchMappedList(mappedPage);
  }, [mappedPage]);

  const resetConsumerState = () => {
    setConsumer(null); setBillMappings([]); setShowConsumerSection(false);
    setConsumerNotFound(false); setIncludeArrear(false); setArrearReceipt("");
    setSharedReceipt(""); setArrearInput(""); setReceiptDate(getTodayISO());
    setTotalMadePayment("");
    setIsAdjustFromAdvance(false);
    setAdjustFromAdvanceAmount("");
  };

  const loadConsumer = async (connectionNo: string) => {
    resetConsumerState();
    try {
      const res  = await axiosInstance.post(API_URLS.consumerDetail, { street_id: Street_id, connection_number: connectionNo });
      const data = res.data?.data;
      if (!data) {
        setConsumerNotFound(true);
        toast.error(`No consumer found: ${connectionNo}`, { position: "top-right", autoClose: 4000 });
        return;
      }
      const bills: Record<string, BillEntry> = data.bills || {};
      const pendingBills: BillMapping[] = Object.entries(bills)
        .filter(([,bill]) => !bill.is_paid)
        .map(([,bill]) => ({ label: billMonthToLabel(bill.bill_month), billMonth: bill.bill_month, amount: bill.bill_amount, billId: bill.id, billNumber: bill.bill_number, selected: false }))
        .sort((a,b) => new Date(a.billMonth).getTime() - new Date(b.billMonth).getTime());
      setConsumer({ connectionNo: data.connection_number, holdingNo: data.holding_number||"", category: data.consumer_category_name||data.consumer_category_initial||"", ferule: data.ferule_size||"", arrear: Number(data.opening_due_amount||0), name: data.consumer_name, is_opening_due_cleared: data.is_opening_due_cleared, bills, phone_number: data.phone_number, advance_amount: Number(data.advance_amount || 0) });
      setBillMappings(pendingBills);
      setConsumerNotFound(false);
      setShowConsumerSection(true);
      consumerSelectedRef.current = true;
      skipSearchRef.current = true;
      setNameQuery(originalNameQueryRef.current);
      setShowNameList(false);
      setConsumerList([]);
      toast.success("Consumer fetched successfully", { position: "top-right", autoClose: 2000 });
    } catch {
      setConsumerNotFound(true);
      toast.error(`No consumer found: ${connectionNo}`, { position: "top-right", autoClose: 4000 });
    }
  };

  const handleSelectFromList = async (item: ConsumerListItem) => {
    setShowNameList(false);
    consumerSelectedRef.current = true;
    skipSearchRef.current = true;
    await loadConsumer(item.connection_number);
  };

  const handleSearchButtonClick = () => {
    if (!nameQuery.trim()) {
      toast.warn("Please enter a name, connection number or phone number", { position: "top-right", autoClose: 3000 });
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    consumerSelectedRef.current = false;
    setNameListPage(1);
    fetchConsumerList(nameQuery, 1);
    setShowNameList(true);
  };

  const handleClearConsumer = () => {
    consumerSelectedRef.current = false;
    resetConsumerState();
    setNameQuery("");
    originalNameQueryRef.current = "";
    setShowNameList(false);
    setConsumerList([]);
  };

  const handleArrearToggle = (checked: boolean) => {
    setIncludeArrear(checked);
    if (!checked) { setArrearReceipt(""); setArrearInput(""); setBillMappings(prev=>prev.map(m=>({...m,selected:false}))); setSharedReceipt(""); }
  };

  const handleBillClick = (clickedIndex: number) => {
    if (!monthTabsEnabled) return;
    setBillMappings(prev => {
      const firstSel = prev.findIndex(m => m.selected);
      const lastSel  = prev.reduce((last,m,i) => m.selected?i:last, -1);
      const isSel    = prev[clickedIndex].selected;
      if (firstSel === -1) {
        if (clickedIndex !== 0) { setTimeout(()=>toast.warn("Please start from the first month",{position:"top-right",autoClose:2000,toastId:"sw"}),0); return prev; }
        return prev.map((m,i) => i===0?{...m,selected:true}:m);
      }
      if (isSel) {
        const upd = prev.map((m,i) => i>=clickedIndex?{...m,selected:false}:m);
        if (!upd.some(m=>m.selected)) setTimeout(()=>setSharedReceipt(""),0);
        return upd;
      }
      if (clickedIndex !== lastSel+1) { setTimeout(()=>toast.warn("Select months in sequence",{position:"top-right",autoClose:2000,toastId:"sw"}),0); return prev; }
      return prev.map((m,i) => i===clickedIndex?{...m,selected:true}:m);
    });
  };

  const handleSave = async () => {
    if (!consumer || isSubmittingRef.current) return;
    if (!canSave) { toast.error("Please fill all required fields", { position: "top-right", autoClose: 3000 }); return; }
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const wid = consumer.bills[Object.keys(consumer.bills)[0]]?.water_connection_id;
      const adjAmt = isAdjustFromAdvance ? Number(adjustFromAdvanceAmount) || 0 : 0;
      await axiosInstance.post(API_URLS.collectormapping, {
        arrear_due_till_march: hasArrear && includeArrear ? Number(arrearInput) || 0 : 0,
        arrear_receipt_number: hasArrear && includeArrear ? arrearReceipt : undefined,
        connection_number: consumer.connectionNo,
        water_connection_id: wid,
        monthly_data: selectedBills.map((b) => ({ water_connection_bill_id: b.billId, monthly_charge_amount: b.amount, receipt_number: sharedReceipt })),
        category_of_consumer: consumer.category,
        ferule_size: consumer.ferule,
        rate: selectedBills.length > 0 ? selectedBills[0].amount : 0,
        total_month_number: selectedBills.length,
        receipt_date: receiptDate,
        actual_bill: totalEntered,
        total_made_payment: totalPaidNum,
        adjusted_from_advance: adjAmt,
      });
      const sn = consumer.connectionNo;
      handleClearConsumer();
      await fetchCollectorDetails();
      setMappedPage(1);
      await fetchMappedList(1);
      const advanceAmount = (totalPaidNum + adjAmt) - totalEntered;
      if (adjAmt > 0) {
        if (advanceAmount > 0) {
          toast.success(`Mapping Successful! ₹${totalPaidNum} cash + ₹${adjAmt} adjusted from advance (including ₹${advanceAmount} new advance) for ${sn}`, { position: "top-right", autoClose: 4000 });
        } else {
          toast.success(`Mapping Successful! ₹${totalPaidNum} cash + ₹${adjAmt} adjusted from advance for ${sn}`, { position: "top-right", autoClose: 4000 });
        }
      } else {
        if (advanceAmount > 0) {
          toast.success(`Mapping Successful! ₹${totalPaidNum} (including ₹${advanceAmount} advance) for ${sn}`, { position: "top-right", autoClose: 3000 });
        } else {
          toast.success(`Mapping Successful! ₹${totalPaidNum} for ${sn}`, { position: "top-right", autoClose: 3000 });
        }
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || "Something went wrong.", { position: "top-right", autoClose: 4000 });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = (m: MappedListEntry) => {
    const monthLabels = (m.monthly_data || [])
      .filter(md => !md.is_arrear_bill && md.water_connection_bill_id)
      .map(md => {
        const parts = (md.bill_month || "").split(/[\s-]/);
        if (parts.length >= 2) {
          const mon = parts[0];
          const yr = parts[parts.length - 1];
          return `${mon}-${yr.length === 4 ? yr.slice(2) : yr}`;
        }
        return md.bill_month || "";
      })
      .filter(Boolean);
    const date = m.created_at
      ? new Date(m.created_at).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})
      : new Date().toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});
    const totalAmt = Number(m.total_amount_at_this_stage || 0);

    generateTamraliptaReceipt({
      receiptNumber:    m.receipt_number && m.receipt_number !== "NA" ? m.receipt_number : "—",
      consumerName:     m.consumer_name || "",
      connectionNumber: m.connection_number,
      holdingNumber:    m.holding_number || "",
      wardNumber:       m.ward_no || "",
      months:           monthLabels,
      amount:           totalAmt,
      amountInWords:    numToWords(totalAmt),
      collectedDate:    m.receipt_date
        ? new Date(m.receipt_date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : date,
      category:         m.category_of_consumer || "",
      feruleSize:       m.ferule_size || "",
      arrearAmount:     Number(m.arrear_due_till_march || 0),
      monthlyAmount:    Number(m.total_monthly_charge_amount || 0),
      phoneNumber:      m.phone_number ? String(m.phone_number) : "",
      advanceAmountPaid: Number(m.total_advance_amount || 0)
    } as TamraliptaReceiptData);

    toast.success("Receipt PDF downloaded!", { position: "top-right", autoClose: 2000 });
  };

  const handleMappedPageChange = (_: React.ChangeEvent<unknown>, v: number) => setMappedPage(v);

  const Name = localStorage.getItem("user_name");
  const paginatedList = mappedList;

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      <h1 className="text-2xl font-bold mb-6">Counter Collector - {Name}</h1>
      <AnimatedCards data={cashDetails} />

      {/* ── Search Section ── */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-4">
        <h3 className="text-lg font-semibold mb-4">Search Consumer</h3>
        <div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by Name, Connection No, Phone No"
                className="border px-3 py-2 rounded-lg w-full pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={nameQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  consumerSelectedRef.current = false;
                  setNameQuery(value);
                  originalNameQueryRef.current = value;
                  setNameListPage(1);
                  if (!value.trim()) { setShowNameList(false); setConsumerList([]); }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearchButtonClick();
                    return;
                  }
                  if (e.key === "Backspace") {
                    consumerSelectedRef.current = false;
                    const newVal = nameQuery.slice(0, -1);
                    if (newVal.trim()) {
                      if (debounceRef.current) clearTimeout(debounceRef.current);
                      setNameListPage(1);
                      fetchConsumerList(newVal, 1);
                      setShowNameList(true);
                    } else {
                      setShowNameList(false);
                      setConsumerList([]);
                    }
                  } else {
                    consumerSelectedRef.current = false;
                  }
                }}
                onFocus={() => {
                  if (nameQuery.trim().length > 0) {
                    if (debounceRef.current) clearTimeout(debounceRef.current);
                    setNameListPage(1);
                    fetchConsumerList(nameQuery, 1);
                    setShowNameList(true);
                  }
                }}
              />
              {nameQuery && (
                <button
                  onClick={handleClearConsumer}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-500 text-lg"
                >
                  ×
                </button>
              )}
              {consumerListLoading && (
                <div className="absolute right-8 top-1/2 -translate-y-1/2">
                  <svg className="animate-spin w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                </div>
              )}
            </div>
            <button
              onClick={handleSearchButtonClick}
              className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"/>
              </svg>
              Search
            </button>
          </div>
        </div>
      </div>

      {/* ── Inline Search Results Table ── */}
      {showNameList && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-4">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-800">Search Results</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {consumerListLoading
                  ? "Searching..."
                  : `${totalConsumers} consumer${totalConsumers !== 1 ? "s" : ""} found for "${nameQuery}"`}
              </p>
            </div>
            <button type="button" onClick={() => setShowNameList(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none" title="Close">×</button>
          </div>

          {consumerListLoading ? (
            <div className="flex items-center justify-center py-10">
              <svg className="animate-spin w-6 h-6 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              <span className="text-sm text-gray-500">Loading consumers...</span>
            </div>
          ) : consumerList.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <p className="text-gray-400 text-sm">No results for "<span className="font-medium text-gray-600">{nameQuery}</span>"</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Consumer Name</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Connection No.</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone No.</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ward</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Street</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                    <th className="text-center py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {consumerList.map((item, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-blue-50 transition-colors cursor-pointer group" onClick={() => handleSelectFromList(item)}>
                      <td className="py-3 px-4 text-xs text-gray-400 font-medium">{(nameListPage - 1) * nameListPerPage + i + 1}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600 font-bold text-xs group-hover:bg-blue-600 group-hover:text-white transition-colors">
                            {item.consumer_name?.slice(0,1)?.toUpperCase() || "?"}
                          </div>
                          <span className="font-semibold text-gray-800">{item.consumer_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-gray-600">{item.connection_number}</td>
                      <td className="py-3 px-4 text-xs text-gray-500">
                        {item.phone_number?.trim()
                          ? `+91 ${item.phone_number}`
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-500">{item.ward_name || <span className="text-gray-300">—</span>}</td>
                      <td className="py-3 px-4 text-xs text-gray-500">
                        {item.street_no ? <>{item.street_no}{item.street_name ? ` · ${item.street_name}` : ""}</> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-3 px-4">
                        {(item.consumer_category_name || item.category)
                          ? <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">{item.consumer_category_name || item.category}</span>
                          : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleSelectFromList(item); }} className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">Select</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!consumerListLoading && totalNamePages > 1 && (
            <div className="border-t border-gray-100 px-4 py-2.5 flex items-center justify-between bg-gray-50">
              <span className="text-xs text-gray-400">{totalConsumers} results · Page {nameListPage} of {totalNamePages}</span>
              <div className="flex items-center gap-1">
                <button type="button" disabled={nameListPage === 1} onClick={() => setNameListPage(p => p - 1)}
                  className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <svg className="w-3.5 h-3.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
                </button>
                <span className="text-xs text-gray-600 px-1 font-medium">{nameListPage} / {totalNamePages}</span>
                <button type="button" disabled={nameListPage === totalNamePages} onClick={() => setNameListPage(p => p + 1)}
                  className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <svg className="w-3.5 h-3.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Consumer Not Found ── */}
      {consumerNotFound && !showConsumerSection && (
        <div className="bg-white shadow-md rounded-lg p-10 mb-4 flex flex-col items-center justify-center border border-red-100">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </div>
          <p className="text-lg font-semibold text-red-600">No connection found</p>
          <p className="text-sm text-gray-400 mt-1">Consumer does not exist in the system</p>
        </div>
      )}

      {/* ── Consumer Details & Bill Mapping ── */}
      {showConsumerSection && consumer && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-4">
          <h3 className="text-lg font-semibold mb-6">Consumer Details & Bill Mapping</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[
              {label:"Connection Number", value:consumer.connectionNo},
              {label:"Consumer Name",     value:consumer.name||""},
              {label:"Holding Number",    value:consumer.holdingNo},
              {label:"Category",          value:consumer.category},
              {label:"Ferrule Size",       value:`${consumer.ferule}"`},
              {label:"Phone number",      value:consumer.phone_number},
            ].map(f=>(
              <div key={f.label}>
                <label className="block text-sm font-medium mb-1">{f.label}</label>
                <input type="text" value={f.value} readOnly disabled className="w-full border rounded-md px-4 py-2 bg-gray-100"/>
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium mb-1">Arrear Due</label>
              <div className="flex items-center gap-3">
                <input type="text" value={`₹ ${arrearAmount}`} readOnly disabled className={`flex-1 border rounded-md px-4 py-2 font-semibold ${arrearAmount>0?"bg-red-50 text-red-600 border-red-200":"bg-gray-100 text-gray-500"}`}/>
                {hasArrear&&(
                  <label className={`flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-md border-2 transition-all ${includeArrear?"bg-orange-100 border-orange-500 text-orange-700":"bg-white border-gray-300 text-gray-600 hover:border-orange-400"}`}>
                    <input type="checkbox" checked={includeArrear} onChange={e=>handleArrearToggle(e.target.checked)} className="w-4 h-4 accent-orange-500 cursor-pointer"/>
                    <span className="text-xs font-semibold whitespace-nowrap">Clear Arrear</span>
                  </label>
                )}
              </div>
              {hasArrear&&<p className="text-xs text-orange-500 mt-1">Click the Arrear tab above to include arrear in payment</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Advance Amount Balance</label>
              <input type="text" value={`₹ ${consumer.advance_amount || 0}`} readOnly disabled
                className={`w-full border rounded-md px-4 py-2 font-semibold bg-gray-100 ${Number(consumer.advance_amount || 0) > 0 ? "text-green-600 border-green-200 bg-green-50/20 font-bold" : "text-gray-500"}`} />
            </div>
          </div>

          {billMappings.length===0&&!hasArrear?(
            <div className="text-center py-10 bg-green-50 rounded-lg border border-green-200">
              <p className="text-green-700 font-semibold text-lg">No pending bills for this consumer</p>
            </div>
          ):(
            <div className="mb-6">
              {hasRemainingDues && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2.5 text-amber-800 text-xs font-semibold">
                  <span>Advance payment is not allowed when there are unpaid due months. Please select and pay all due months to enable advance payments.</span>
                </div>
              )}
              <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200 pb-2">
                {hasArrear&&(
                  <button type="button" onClick={()=>handleArrearToggle(!includeArrear)} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${includeArrear?"bg-orange-500 text-white hover:bg-orange-600":"bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-300"}`}>
                    {includeArrear&&<span className="w-4 h-4 flex-shrink-0 rounded border-2 border-white bg-white flex items-center justify-center"><svg className="w-2.5 h-2.5 text-orange-500" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span>}
                    Arrear
                  </button>
                )}
                {billMappings.map((bill,idx)=>{
                  const locked   = hasArrear&&!arrearFullyPaid;
                  const prevSel  = idx===0||billMappings[idx-1].selected;
                  const canNext  = !locked&&!bill.selected&&prevSel;
                  const clickable= !locked&&(bill.selected||canNext);
                  return(
                    <button key={bill.label} type="button" onClick={()=>clickable&&handleBillClick(idx)}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${locked?"bg-gray-100 text-gray-400 cursor-not-allowed opacity-60 border border-gray-200":bill.selected?"bg-blue-600 text-white hover:bg-blue-700 cursor-pointer":canNext?"bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 cursor-pointer":"bg-gray-50 text-gray-300 cursor-not-allowed border border-gray-100"}`}>
                      {bill.selected&&<span className="w-4 h-4 flex-shrink-0 rounded border-2 border-white bg-white flex items-center justify-center"><svg className="w-2.5 h-2.5 text-blue-600" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span>}
                      {bill.label}
                    </button>
                  );
                })}
              </div>

              {selectedBills.length===0&&!(hasArrear&&includeArrear)&&(
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-gray-500 text-sm mb-4">
                  {hasArrear&&!includeArrear?"Select the Arrear tab first to unlock month selection":"Click the first month tab to begin selection"}
                </div>
              )}

              {anythingSelected && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 mt-4 mb-6">
                  <h4 className="text-sm font-bold text-gray-700 mb-3">Payment Summary</h4>
                  
                  {Number(consumer.advance_amount || 0) > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={isAdjustFromAdvance}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setIsAdjustFromAdvance(checked);
                              if (checked) {
                                const maxAdj = Math.min(consumer.advance_amount || 0, totalEntered);
                                setAdjustFromAdvanceAmount(String(maxAdj));
                              } else {
                                setAdjustFromAdvanceAmount("");
                              }
                            }}
                            className="w-4.5 h-4.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                          <span className="text-sm font-bold text-blue-800">Adjust from Advance Amount</span>
                        </label>
                        <span className="text-xs text-blue-600 font-semibold">(Available: ₹ {consumer.advance_amount})</span>
                      </div>
                      {isAdjustFromAdvance && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-blue-700">Adjustment Amount:</span>
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-xs">₹</span>
                            <input
                              type="number"
                              value={adjustFromAdvanceAmount}
                              onChange={(e) => {
                                const rawVal = e.target.value;
                                const val = Number(rawVal);
                                const maxAdj = Math.min(consumer.advance_amount || 0, totalEntered);
                                if (rawVal.startsWith("-") || val < 0) {
                                  toast.warn("Adjustment amount cannot be negative.", { toastId: "neg-warn" });
                                  setAdjustFromAdvanceAmount("0");
                                  return;
                                }
                                if (val > maxAdj) {
                                  setAdjustFromAdvanceAmount(String(maxAdj));
                                } else {
                                  setAdjustFromAdvanceAmount(rawVal);
                                }
                              }}
                              className="w-28 pl-6 pr-2 py-1 border border-blue-300 rounded text-sm font-bold text-right focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white no-spinner [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
                    
                    {/* Col 1: Selected Items & Details */}
                    <div className="flex flex-col gap-1">
                      <p className="text-xs font-medium text-gray-500">Selected Items</p>
                      <div className="flex flex-wrap gap-1.5">
                        {hasArrear && includeArrear && (
                          <span className="flex items-center gap-1 px-2.5 py-1 bg-orange-500 text-white text-xs rounded-full font-medium">
                            Arrear
                            <button type="button" onClick={() => handleArrearToggle(false)} className="w-3.5 h-3.5 rounded-full bg-white bg-opacity-20 hover:bg-red-500 flex items-center justify-center font-bold transition-colors leading-none text-white">×</button>
                          </span>
                        )}
                        {selectedBills.map((b) => (
                          <span key={b.label} className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white text-xs rounded-full font-medium">
                            {b.label}
                            <button type="button" onClick={() => handleBillClick(billMappings.findIndex(m => m.label === b.label))} className="w-3.5 h-3.5 rounded-full bg-white bg-opacity-20 hover:bg-red-500 flex items-center justify-center font-bold transition-colors leading-none text-white">×</button>
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Col 2: Receipt Details */}
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col gap-1">
                        <p className="text-xs font-semibold text-gray-500">Receipt Number</p>
                        <input type="text" value={arrearReceipt} onChange={e => { setArrearReceipt(e.target.value); setSharedReceipt(e.target.value); }} placeholder="Enter receipt number" className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" autoFocus maxLength={25} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <p className="text-xs font-semibold text-gray-500">Receipt Date</p>
                        <input
                          type="date"
                          value={receiptDate}
                          max={new Date().toISOString().split("T")[0]}
                          onChange={(e) => setReceiptDate(e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                        />
                      </div>
                    </div>

                    {/* Col 3: Arrear / Bill Details */}
                    <div className="flex flex-col gap-3">
                      {hasArrear && includeArrear && selectedBills.length === 0 ? (
                        <div className="flex flex-col gap-1">
                          <p className="text-xs font-semibold text-gray-500">Arrear Amount to Pay</p>
                          <input
                            type="number"
                            value={arrearInput}
                            onChange={(e) => {
                              const rawVal = e.target.value;
                              const val = Number(rawVal);
                              if (rawVal.startsWith("-") || val < 0) {
                                toast.warn("Arrear payment cannot be negative.", { toastId: "neg-warn" });
                                setArrearInput("");
                                return;
                              }
                              setArrearInput(val > arrearAmount ? String(arrearAmount) : rawVal);
                            }}
                            placeholder={`₹${arrearAmount}`}
                            className={`w-full border rounded px-2 py-1.5 text-sm font-bold text-right focus:outline-none focus:ring-2 bg-white no-spinner [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${arrearInput !== "" && Number(arrearInput) === arrearAmount ? "border-green-400 focus:ring-green-400" : "border-orange-300 focus:ring-orange-400"}`}
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <p className="text-xs font-semibold text-gray-500">Actual Bill Amount</p>
                          <div className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm font-bold text-gray-700 bg-gray-100 flex items-center justify-between">
                            <span>₹</span>
                            <span>{totalEntered.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Col 4: Made Payment & Advance calculation */}
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col gap-1">
                        <p className="text-xs font-semibold text-blue-700">Total Made Payment</p>
                        <input
                          type="number"
                          value={totalMadePayment}
                          onChange={(e) => {
                            const val = e.target.value;
                            const numVal = Number(val) || 0;
                            const cashRequired = totalEntered - adjAmountNum;
                            if (val.startsWith("-") || numVal < 0) {
                              toast.warn("Payment amount cannot be negative.", { toastId: "neg-warn" });
                              setTotalMadePayment("");
                              return;
                            }
                            if (hasRemainingDues && numVal > cashRequired) {
                              setTotalMadePayment(String(cashRequired));
                              toast.warn("Advance payment is not allowed when there are unpaid due months.", { toastId: "adv-warn" });
                            } else {
                              setTotalMadePayment(val);
                            }
                          }}
                          placeholder={`₹${totalEntered - adjAmountNum}`}
                          className={`w-full border rounded px-3 py-1.5 text-sm font-bold text-right focus:outline-none focus:ring-2 bg-white no-spinner [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${totalPaidNum < (totalEntered - adjAmountNum) ? "border-red-400 focus:ring-red-400" : "border-blue-400 focus:ring-blue-400"}`}
                        />
                        {totalPaidNum < (totalEntered - adjAmountNum) && (
                          <p className="text-xs text-red-500 mt-1">Paid amount cannot be less than cash required (₹{totalEntered - adjAmountNum})</p>
                        )}
                        {hasRemainingDues && totalPaidNum > (totalEntered - adjAmountNum) && (
                          <p className="text-xs text-red-500 mt-1">Advance payment is not allowed when there are unpaid due months</p>
                        )}
                      </div>
                      
                      {totalPaidNum > (totalEntered - adjAmountNum) && (
                        <div className="flex flex-col gap-1">
                          <p className="text-xs font-semibold text-green-700">New Advance Amount</p>
                          <div className="w-full border border-green-200 rounded px-3 py-1.5 text-sm font-bold text-green-700 bg-green-50 flex items-center justify-between">
                            <span>₹</span>
                            <span>{(totalPaidNum - (totalEntered - adjAmountNum)).toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button onClick={handleClearConsumer} className="px-6 py-3 rounded-lg font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors">Cancel</button>
            <button onClick={handleSave} disabled={!canSave||isSubmitting} className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors">
              {isSubmitting?"Saving...":!anythingSelected?"Select arrear or at least one month to save":hasArrear&&includeArrear&&Number(arrearInput)<=0?"Enter arrear amount to proceed":arrearReceipt.trim()===""?"Enter receipt number":totalPaymentApplied<totalEntered?"Paid amount is less than bill amount":`Save Payment (₹${totalPaidNum}${isAdjustFromAdvance ? ` + ₹${adjAmountNum} from Advance` : ""}) for ${consumer.name||consumer.connectionNo} (${consumer.connectionNo})`}
            </button>
          </div>
        </div>
      )}

      {/* ── Monthly Collections Table ── */}
      {!showConsumerSection && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden mt-2">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-800">Monthly Collections</h3>
              <p className="text-xs text-gray-400 mt-0.5">{new Date().toLocaleDateString("en-GB",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</p>
            </div>
            <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">{mappedList.length} mapped</span>
          </div>
          <div className="p-6">
            {mappedList.length===0?(
              <div className="text-center py-10 text-gray-400">No Data Found</div>
            ):(
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {["Connection ID","Collected Months","Collected Amount","Receipt No","Collected Date","Action"].map(h=>(
                        <th key={h} className={`py-2.5 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wide ${h==="Action"?"text-center":"text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedList.map((m,i)=>{
                      const mc  = Number(m.total_month_number)||0;
                      const md2 = m.receipt_date ? new Date(m.receipt_date).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "—";
                      const ml  = (m.monthly_data || [])
                        .filter(md => !md.is_arrear_bill && md.water_connection_bill_id)
                        .map(md => {
                          const parts = (md.bill_month || "").split(/[\s-]/);
                          if (parts.length >= 2) {
                            const mon = parts[0];
                            const yr = parts[parts.length - 1];
                            return `${mon}-${yr.length === 4 ? yr.slice(2) : yr}`;
                          }
                          return md.bill_month || "";
                        })
                        .filter(Boolean);
                      return(
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-3 font-mono font-semibold text-gray-800">{m.connection_number}</td>
                          <td className="py-3 px-3">
                            {mc>0?(
                              <div className="relative group inline-block">
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold cursor-default">{mc} month{mc>1?"s":""}</span>
                                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-20 hidden group-hover:flex flex-col bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-max">
                                  <p className="font-semibold mb-1.5 text-gray-300 text-xs">Months mapped:</p>
                                  <div className="flex flex-wrap gap-1">{ml.map((mo,mi)=><span key={mi} className="px-1.5 py-0.5 bg-blue-500 rounded text-white text-xs font-medium">{mo}</span>)}</div>
                                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"/>
                                </div>
                              </div>
                            ):<span className="text-gray-400 text-xs">Arrear only</span>}
                          </td>
                          <td className="py-3 px-3 font-bold text-green-600">₹ {Number(m.total_amount_at_this_stage||0).toLocaleString()}</td>
                          <td className="py-3 px-3 font-mono text-xs text-gray-600">{m.receipt_number&&m.receipt_number!=="NA"?m.receipt_number:<span className="text-gray-300">—</span>}</td>
                          <td className="py-3 px-3 text-xs text-gray-500">{md2}</td>
                          <td className="py-3 px-3">
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={e=>{e.stopPropagation();setSelectedMapped(m);}} className="w-7 h-7 rounded-md bg-blue-50 border border-blue-200 hover:bg-blue-100 flex items-center justify-center transition-colors" title="View">
                                <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                              </button>
                              <button onClick={e=>{e.stopPropagation();handleDownloadPDF(m);}} className="w-7 h-7 rounded-md bg-gray-50 border border-gray-400 hover:bg-gray-200 flex items-center justify-center transition-colors" title="Download Receipt">
                                <svg className="w-4 h-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-200">
                      <td className="py-2.5 px-3 text-xs text-gray-500 font-medium">Today's Map Amount</td>
                      <td/><td className="py-2.5 px-3 font-bold text-blue-700 text-sm">₹ {mappedList.reduce((s,item)=>s+Number(item.total_amount_at_this_stage||0),0).toLocaleString()}</td>
                      <td colSpan={3}/>
                    </tr>
                  </tfoot>
                </table>

                <div className="flex justify-center mt-6">
                  <Pagination
                    count={mappedTotalPages}
                    page={mappedPage}
                    onChange={handleMappedPageChange}
                    color="primary"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Transaction Detail Modal ── */}
      {selectedMapped && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setSelectedMapped(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700">
              <h2 className="text-base font-bold text-white">Transaction Details</h2>
              <button onClick={() => setSelectedMapped(null)} className="w-7 h-7 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-bold transition-colors">×</button>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-bold">{selectedMapped?.consumer_name?.slice(0,1)?.toUpperCase() || ""}</span>
                </div>
                <div>
                  <p className="font-bold text-gray-800">{selectedMapped.connection_number}</p>
                  <p className="text-xs text-gray-500">{selectedMapped.category_of_consumer} · Ferule: {selectedMapped.ferule_size}</p>
                  {selectedMapped.phone_number && (
                    <p className="text-xs text-gray-500 mt-0.5">📞 {selectedMapped.phone_number}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-green-50 rounded-xl p-3 col-span-2">
                  <p className="text-xs text-gray-400 mb-1">Total Amount Collected</p>
                  <p className="font-bold text-green-700 text-xl">₹ {Number(selectedMapped.total_amount_at_this_stage).toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">Monthly Charge</p>
                  <p className="font-semibold text-gray-800 text-sm">₹ {Number(selectedMapped.total_monthly_charge_amount).toLocaleString()}</p>
                </div>
                <div className="bg-orange-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">Arrear Due</p>
                  <p className="font-semibold text-orange-600 text-sm">₹ {Number(selectedMapped.arrear_due_till_march).toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">Rate</p>
                  <p className="font-semibold text-gray-800 text-sm">₹ {Number(selectedMapped.rate).toLocaleString()}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">Advance Balance</p>
                  <p className="font-semibold text-green-700 text-sm">₹ {Number(selectedMapped.advance_amount || 0).toLocaleString()}</p>
                </div>
                {Number(selectedMapped.total_advance_amount || 0) !== 0 && (
                  <div className={`${Number(selectedMapped.total_advance_amount) > 0 ? "bg-green-50 border border-green-100" : "bg-blue-50 border border-blue-100"} rounded-xl p-3 col-span-2`}>
                    <p className="text-xs text-gray-400 mb-1">
                      {Number(selectedMapped.total_advance_amount) > 0 ? "New Advance Paid" : "Advance Adjusted"}
                    </p>
                    <p className={`font-bold text-sm ${Number(selectedMapped.total_advance_amount) > 0 ? "text-green-700" : "text-blue-700"}`}>
                      ₹ {Math.abs(Number(selectedMapped.total_advance_amount)).toLocaleString()}
                    </p>
                  </div>
                )}
                <div className="bg-blue-50 rounded-xl p-3 col-span-2">
                  <p className="text-xs text-gray-400 mb-2">Collected Months</p>
                  <div className="flex flex-wrap gap-1">
                    {(() => {
                      const monthItems = (selectedMapped.monthly_data || [])
                        .filter(md => !md.is_arrear_bill && md.water_connection_bill_id)
                        .map(md => {
                          const parts = (md.bill_month || "").split(/[\s-]/);
                          if (parts.length >= 2) {
                            const mon = parts[0];
                            const yr = parts[parts.length - 1];
                            return `${mon}-${yr.length === 4 ? yr.slice(2) : yr}`;
                          }
                          return md.bill_month || "";
                        })
                        .filter(Boolean);

                      if (monthItems.length === 0) return <span className="text-gray-400 text-xs">Arrear only</span>;

                      return monthItems.map((lbl, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded font-medium">{lbl}</span>
                      ));
                    })()}
                  </div>
                </div>
              </div>
              {selectedMapped.receipt_number && selectedMapped.receipt_number !== "NA" && (
                <div className="bg-blue-50 rounded-xl p-3 flex items-center justify-between">
                  <p className="text-xs text-gray-400">Receipt No.</p>
                  <p className="font-mono font-semibold text-blue-700 text-sm">{selectedMapped.receipt_number}</p>
                </div>
              )}
             <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-end text-xs text-gray-400">
              <span>
                {new Date(selectedMapped.receipt_date).toLocaleString("en-GB", {
                 day: "2-digit",
                 month: "short",
                 year: "numeric"
                 })}
          </span>
</div>
            </div>
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
              <button onClick={() => setSelectedMapped(null)} className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm font-medium text-white transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer/>
    </div>
  );
};

export default CounterCollector;